import {
  addDays,
  format,
  parse,
  setHours,
  setMinutes,
  startOfDay,
} from "date-fns";
import {
  GENERAL_CLEANING_WEEKLY_RATES,
  GENERAL_CLEANING_FORTNIGHTLY_RATES,
  BASE_RATE_CENTS,
  PRICING_TIERS,
  type PricingTier,
} from "../constants/pricing";
import type {
  Availability,
  BookingModel,
  SessionModel,
  AvailabilityModel,
  Slot,
} from "../types/models";
import { BOOKING_MAX_DAYS_AHEAD } from "../constants/booking.js";
import { formatTime24Hour } from "./time";

export const displayFrequencyNameMapping: Record<string, string> = {
  weekly: "Weekly",
  one_time: "One-time",
  fortnightly: "Fortnightly",
  quarterly: "Quarterly",
};

export const displayServicePictureMapping: Record<number, string> = {
  1: "/services/general-cleaning.webp",
  2: "/services/aircon-servicing.webp",
  3: "/services/general-cleaning.webp",
  4: "/services/general-cleaning.webp",
  5: "/services/general-cleaning.webp",
};

/* 
    Refactored session filtering logic to guarantee boolean return values and reduce repeated
    Date() instantiation. This prevents silent undefined cases and improves logical readability
*/
export function filterSession(filter: string, session: SessionModel) {
  const now = new Date();
  if (session.status === "cancelled" || session.status === "expired") return false;
  const sessionStart = parse(session.start_time, "HH:mm", session.date);
  return filter === "Past"
    ? sessionStart < now
    : filter === "Upcoming"
      ? sessionStart >= now
      : filter === "All"
        ? true
        : false;
}

export function sortSessions(sortBy: string, a: SessionModel, b: SessionModel) {
  const [aStartHours, aStartMinutes] = a.start_time.split(":");
  const aStart = setMinutes(
    setHours(a.date, Number(aStartHours)),
    Number(aStartMinutes),
  );
  const [bStartHours, bStartMinutes] = b.start_time.split(":");
  const bStart = setMinutes(
    setHours(b.date, Number(bStartHours)),
    Number(bStartMinutes),
  );
  switch (sortBy) {
    case "Past":
      return bStart.getTime() - aStart.getTime();
    case "Upcoming":
      return aStart.getTime() - bStart.getTime();
    default:
      return 0;
  }
}

function buildSessionDateTime(
  session: SessionModel,
  which: "start" | "end",
) {
  const time = which === "start" ? session.start_time : session.end_time;
  if (typeof time !== "string") return new Date(Number.NaN);

  const [hours, minutes] = time.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return new Date(Number.NaN);
  }

  const date = new Date(session.date);
  if (Number.isNaN(date.getTime())) return date;

  return setMinutes(setHours(date, hours), minutes);
}

export function AdaptBookingsToEvents(bookings: BookingModel[]) {
  return bookings
    .map((booking) =>
      (booking.sessions ?? []).flatMap((session) => {
        const start = buildSessionDateTime(session, "start");
        const end = buildSessionDateTime(session, "end");
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          return [];
        }
        return {
          title: booking.homeowner_name,
          start,
          end,
          booking,
          session,
        };
      }),
    )
    .flat();
}

export function formatDisplayLocation(
  address?: string | number | null,
  postal?: string | number | null,
  floor_unit?: string | number | null,
): string {
  const addrStr = String(address ?? "");
  const postalStr = String(postal ?? "");
  const unitStr = String(floor_unit ?? "");

  const cleanAddress = addrStr
    .trim()
    .replace(/\s+/g, " ")
    .replace(/,\s*Singapore\s+\d{6}\s*$/i, "")
    .replace(/\s+Singapore\s+\d{6}\s*$/i, "")
    .replace(/,\s*#?[A-Z0-9/-]+\s*$/i, "")
    .replace(/(?:,\s*)?\bSingapore\b\s*$/i, "")
    .replace(/[,\s]+(?:N\/A|NA)\s*$/i, "")
    .trim();
  const cleanPostal = postalStr.replace(/\s+/g, "");
  const cleanUnit = unitStr.trim().replace(/^#/, "");
  const hasUnit = cleanUnit && !/^(NA|N\/A)$/i.test(cleanUnit);

  return [
    cleanAddress || undefined,
    cleanPostal ? `Singapore ${cleanPostal}` : undefined,
    hasUnit ? `#${cleanUnit}` : undefined,
  ].filter(Boolean).join(", ");
}

export type AvailabilityMap = Record<string, Record<string, Availability>>;

export function availabilityAdapter(
  slots: AvailabilityModel[],
): AvailabilityMap {
  return slots.reduce((acc, slot) => {
    if (!slot.cleaners || slot.cleaners.length === 0) return acc;

    const dateKey = format(slot.start_date, "yyyy-MM-dd");
    const startTime = formatTime24Hour(slot.start_time);
    const endTime = formatTime24Hour(slot.end_time);
    if (startTime === "-" || endTime === "-") return acc;

    const timeKey = `${startTime}-${endTime}`;

    if (!acc[dateKey]) acc[dateKey] = {};
    acc[dateKey][timeKey] = {
      start_time: startTime,
      end_time: endTime,
      cleaners: slot.cleaners,
      pricing: slot.pricing,
      pricing_snapshot_id: slot.pricing_snapshot_id,
      pricing_snapshot_slot_id: slot.pricing_snapshot_slot_id,
    };

    return acc;
  }, {} as AvailabilityMap);
}

export function bookedSlotsAdapter(
  slots: AvailabilityModel[],
): AvailabilityMap {
  return slots.reduce((acc, slot) => {
    if (slot.cleaners && slot.cleaners.length > 0) return acc;
    const dateKey = format(slot.start_date, "yyyy-MM-dd");
    const startTime = formatTime24Hour(slot.start_time);
    const endTime = formatTime24Hour(slot.end_time);
    if (startTime === "-" || endTime === "-") return acc;

    const timeKey = `${startTime}-${endTime}`;
    if (!acc[dateKey]) acc[dateKey] = {};
    acc[dateKey][timeKey] = {
      start_time: startTime,
      end_time: endTime,
      cleaners: [],
    };
    return acc;
  }, {} as AvailabilityMap);
}

function toDate(input: Date | string): Date {
  return input instanceof Date ? input : new Date(input);
}

function isWeekendDate(date: Date) {
  const day = date.getDay(); // 0=Sun, 6=Sat
  return day === 0 || day === 6;
}

function isWeekendEveningSlot(slot: Slot) {
  return slot.start_time?.startsWith("18:00");
}

function applyWeekendRule(date: Date, slots: Slot[]) {
  return isWeekendDate(date)
    ? slots.filter((s) => !isWeekendEveningSlot(s))
    : slots;
}

export function getTimeSlotsFromAvailability(
  date: Date | string,
  frequency: string,
  availability: AvailabilityMap,
): Slot[] {
  if (!date || !availability) return [];

  const d = toDate(date);
  const key = format(d, "yyyy-MM-dd");
  if (!availability[key]) return [];

  const filterWeekend = (slots: Slot[]) => applyWeekendRule(d, slots);

  // one_time / quarterly: just return slots for that date
  if (frequency === "one_time" || frequency === "quarterly") {
    const slots = Object.values(availability[key]);
    return filterWeekend(slots);
  }

  // weekly / fortnightly: intersection across interval occurrences
  const interval = frequency === "weekly" ? 7 : 14;
  const slots = Object.values(availability[key]);
  if (!slots || slots.length === 0) return [];

  const availableSlots: Slot[] = [];

  for (const slot of slots) {
    let cleaners = slot.cleaners;
    let currDate = d;

    while (
      addDays(currDate, interval) <=
      addDays(startOfDay(new Date()), BOOKING_MAX_DAYS_AHEAD - 1)
    ) {
      currDate = addDays(currDate, interval);
      const currKey = format(currDate, "yyyy-MM-dd");
      const currSlot = availability[currKey]?.[`${slot.start_time}-${slot.end_time}`];

      if (!currSlot) {
        cleaners = [];
        break;
      }

      cleaners = cleaners.filter((cleaner) =>
        currSlot.cleaners.some((c) => c === cleaner),
      );
    }

    if (cleaners.length > 0) {
      availableSlots.push({ ...slot, cleaners });
    }
  }

  return filterWeekend(availableSlots);
}

export function getCleanerFromSlot(slot: Slot) {
  if (!slot) return -1;
  return slot.cleaners[0] ?? -1;
}

/**
 * The single place that decides which cleaner a slot is booked with (KAN-580).
 *
 * An explicitly requested HomeHelper is returned as-is and is NEVER replaced by
 * whoever happens to be first in `slot.cleaners`. That replacement was the bug:
 * it wrote `cleaner_id` without touching `preferredCleanerId`, so the summary
 * panel kept showing the requested HomeHelper (read from `preferredCleanerId`)
 * while Review showed the substitute (read from `cleaner_id`) — two names on
 * screen at once, and the booking silently went to someone the user did not pick.
 *
 * Not substituting is also the rule the rest of the flow already follows: see
 * `mayAutoSubstituteCleaner` in utils/checkoutErrors.ts, which the checkout
 * conflict handler uses to refuse a swap for a pinned HomeHelper (KAN-550). When
 * the pinned cleaner genuinely cannot serve the slot, the server-side conflict
 * check rejects it and that handler clears the time and asks the user to pick
 * another — a visible, recoverable outcome rather than a silent wrong booking.
 *
 * With no preference there is nothing to protect and no name is displayed, so the
 * first available cleaner is used exactly as before.
 *
 * Callers must not re-implement this ternary — six copies of it had drifted.
 */
export function resolveCleanerForSlot(
  slot: Slot | null | undefined,
  preferredCleanerId: number | null | undefined,
): number {
  if (preferredCleanerId != null && preferredCleanerId > 0) {
    return preferredCleanerId;
  }
  return getCleanerFromSlot(slot as Slot);
}

/**
 * Display name for an assigned cleaner, preferring the live availability response
 * and falling back to a name already known from earlier in the flow (KAN-580).
 *
 * Review resolves the name by searching the current availability response for the
 * assigned id. That response also reflects availability, so a HomeHelper booked
 * elsewhere mid-flow dropped out of it and the name went null — which hid Review's
 * entire "Your HomeHelper" card while the summary panel still named them. A
 * section vanishing reads as a glitch rather than as information.
 *
 * The fallback keeps the card rendered. It does not assert the cleaner is still
 * free: that is checked server-side by validateSelectedSlot and again at checkout,
 * which names the HomeHelper and returns the customer to the schedule step.
 */
export function resolveCleanerDisplayName(
  availableCleaners: ReadonlyArray<{ id: number; name: string }>,
  knownCleaners: ReadonlyArray<{ id: number; name: string }>,
  cleanerId: number | null | undefined,
): string | null {
  if (cleanerId == null || cleanerId <= 0) return null;
  return (
    availableCleaners.find((c) => c.id === cleanerId)?.name ??
    knownCleaners.find((c) => c.id === cleanerId)?.name ??
    null
  );
}

export function calculateGeneralCleaningPrice(
  freq: "one_time" | "weekly" | "fortnightly",
  duration = 3,
) {
  if (freq === "one_time") {
    return (BASE_RATE_CENTS / 100) * duration;
  }

  const rateMap =
    freq === "weekly"
        ? GENERAL_CLEANING_WEEKLY_RATES
        : GENERAL_CLEANING_FORTNIGHTLY_RATES;
  const rate = rateMap[duration as keyof typeof rateMap] ?? rateMap[3];
  return rate * duration;
}

const AIRCON_PRICES: Record<string, number[]> = {
  one_time:  [0, 40, 60, 69, 92, 115, 138, 161, 184, 207, 230],
  quarterly: [0, 160, 200, 216, 288, 360, 432, 504, 576, 648, 720],
};

export function calculateAirconPrice(frequency: string, numUnits: number): number {
  const table = AIRCON_PRICES[frequency];
  if (!table || numUnits < 1 || numUnits > 10) return 0;
  return table[numUnits];
}

export function getPostalCode(
  addressComponents?: google.maps.GeocoderAddressComponent[] | null,
): string | null {
  if (!addressComponents || !Array.isArray(addressComponents)) return null;

  for (const component of addressComponents) {
    if (component.types.includes("postal_code")) {
      return component.long_name || component.short_name || null;
    }
  }
  return null;
}

export function getAddressFromComponents(
  components?: google.maps.GeocoderAddressComponent[] | null,
): string {
  if (!components || !Array.isArray(components)) return "";

  const componentTypesToInclude = ["street_number", "route", "neighborhood", "sublocality_level_1"];
  const parts: string[] = [];

  for (const type of componentTypesToInclude) {
    const comp = components.find((c) => c.types.includes(type));
    if (comp?.long_name) parts.push(comp.long_name);
  }

  return parts.join(" ");
}

export type PostalLookupResult = {
  address: string;
  isHDB: boolean;
  hasUnit: boolean;
};

export async function lookupAddressByPostal(postal: string): Promise<PostalLookupResult> {
  if (!/^\d{6}$/.test(postal)) return { address: "", isHDB: false, hasUnit: true };
  try {
    const res = await fetch(
      `https://www.onemap.gov.sg/api/common/elastic/search?searchVal=${postal}&returnGeom=N&getAddrDetails=Y&pageNum=1`,
    );
    if (!res.ok) return { address: "", isHDB: false, hasUnit: true };
    const data = await res.json();
    if (!data.results?.length) return { address: "", isHDB: false, hasUnit: true };
    const result = data.results[0];
    const raw: string = result.ADDRESS || "";
    const titleCase = (value: string) =>
      value
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

    // Prefer canonical address parts from OneMap to avoid tenant/building noise
    // such as "Pro-teach ... (Jurong)" being appended.
    const blockNo = String(result.BLK_NO || "").trim();
    const roadName = String(result.ROAD_NAME || "").trim();

    let address = "";
    if (blockNo && roadName) {
      address = `${blockNo} ${titleCase(roadName)}`.trim();
    } else {
      const building = String(result.BUILDING || "").trim();
      let stripped = raw.replace(/\s+SINGAPORE\s+\d{6}\s*$/i, "").trim();
      stripped = stripped.replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim();

      if (building && building.toUpperCase() !== "NIL") {
        const escapedBuilding = building.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        stripped = stripped
          .replace(new RegExp(`\\s*${escapedBuilding}\\s*$`, "i"), "")
          .trim();
      }
      address = titleCase(stripped);
    }
    const buildingNil = !result.BUILDING || result.BUILDING.toUpperCase() === "NIL";
    const isHDB = buildingNil;

    // Detect if a floor/unit number is needed.
    // Condos always have a building name → hasUnit = true.
    // For properties where BUILDING = "NIL" (HDB or landed/standalone):
    //   - If the address contains "BLK" it is definitively an HDB block → hasUnit = true.
    //   - Otherwise, use the leading house number as a heuristic:
    //     HDB block numbers are typically >= 100; landed/standalone house numbers are typically < 100.
    let hasUnit = true;
    if (buildingNil) {
      const hasBlkPrefix = /\bBLK\b/i.test(raw);
      if (!hasBlkPrefix) {
        const leadingNum = parseInt(raw.match(/^(\d+)/)?.[1] ?? "999", 10);
        hasUnit = leadingNum >= 100;
      }
    }

    return { address, isHDB, hasUnit };
  } catch {
    return { address: "", isHDB: false, hasUnit: true };
  }
}

/**
 * @param isDateBookable Optional predicate applied instead of the default
 *   "has any slot at all" check. Pass the caller's own duration + preferred-cleaner
 *   filter so the date this returns is one the user can actually book — without it,
 *   a preferred-cleaner selection can land on a date that only has slots for
 *   *other* cleaners.
 */
export function extractEarliestDateFromAvailability(
  availability: AvailabilityMap,
  frequency: string,
  isDateBookable?: (date: Date) => boolean,
): Date | null {
  if (!availability || !frequency) return null;

  const dates = Object.keys(availability)
    .map((dateStr) => parse(dateStr, "yyyy-MM-dd", new Date()))
    .filter((d) => !isNaN(d.getTime()));

  const validDates = dates.filter((date) =>
    isDateBookable
      ? isDateBookable(date)
      : getTimeSlotsFromAvailability(date, frequency, availability).length > 0,
  );

  if (validDates.length === 0) return null;

  validDates.sort((a, b) => a.getTime() - b.getTime());

  return validDates[0];
}

type SessionPhaseColor = "blue" | "green" | "orange" | "red";

export function getSessionPhaseColor({
  isOneTime,
  sessionNumber,
  totalSessions,
}: {
  isOneTime: boolean;
  sessionNumber: number;
  totalSessions: number;
}): SessionPhaseColor {
  // One-time job → always blue
  if (isOneTime || totalSessions <= 1) {
    return "blue";
  }

  // Final session → red
  if (sessionNumber === totalSessions) {
    return "red";
  }

  // Mid-phase logic
  const midStart = Math.ceil(totalSessions * 0.6);

  if (sessionNumber >= midStart) {
    return "orange";
  }

  // Early phase
  return "green";
}

export function isDeepCleaning(serviceName?: string | null) {
  if (!serviceName) return false;

  const normalized = serviceName.toLowerCase();
  return (
    normalized.includes("reno") ||
    normalized.includes("move in") ||
    normalized.includes("move out") ||
    normalized.includes("deep")
  );
}

export function isAdhoc(serviceName?: string | null) {
  if (!serviceName) return false;
  return serviceName.toLowerCase().replace(/[\s-]+/g, "").includes("adhoc");
}

export const EVENT_COLOR_STYLE = {
  phase: {
    blue: { bg: "blue.400", border: "blue.600" },
    green: { bg: "green.400", border: "green.600" },
    orange: { bg: "orange.500", border: "orange.700" },
    red: { bg: "red.500", border: "red.700" },
  },
  deep: {
    bg: "purple.500",
    border: "purple.700",
  },
} as const;

function isPastEvent(session: SessionModel) {
  const end = getSessionDateTime(session, "end");
  return end < new Date();
}

export function getEventColorStyle(
  booking: BookingModel,
  session: SessionModel,
) {
  const baseStyle = (() => {
    if (isDeepCleaning(booking.service_name)) {
      return EVENT_COLOR_STYLE.deep;
    }

    const total = booking.sessions?.length ?? 1;
    const index =
      booking.sessions
        ?.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        )
        .findIndex((s) => s.id === session.id) ?? 0;

    const phase = getSessionPhaseColor({
      isOneTime: booking.frequency === "one_time",
      sessionNumber: index + 1,
      totalSessions: total,
    });

    return EVENT_COLOR_STYLE.phase[phase];
  })();

  const isPast = isPastEvent(session);

  return {
    ...baseStyle,
    opacity: isPast ? 0.85 : 1,
    filter: isPast ? "grayscale(30%)" : "none",
  };
}

export function getSessionDateTime(
  session: SessionModel,
  which: "start" | "end",
) {
  return buildSessionDateTime(session, which);
}

export function formatSessionTimeRange(
  session: SessionModel,
  formatStr: string = "h:mm a",
) {
  const start = getSessionDateTime(session, "start");
  const end = getSessionDateTime(session, "end");

  return `${format(start, formatStr)} - ${format(end, formatStr)}`;
}

export type DayPricingSummary = {
  lowestHourlyRateCents: number;
  lowestTier: PricingTier;
  lowestTierColor: string;
};

export function getDayPricingSummary(
  date: Date | string,
  frequency: string,
  availability: AvailabilityMap,
  preferredCleanerId?: number | null,
): DayPricingSummary | null {
  // Recurring frequencies have fixed pricing — no dynamic tiers
  if (frequency === "weekly" || frequency === "fortnightly") {
    return {
      lowestHourlyRateCents: 2500,
      lowestTier: "normal",
      lowestTierColor: PRICING_TIERS.normal.color,
    };
  }

  const allSlots = getTimeSlotsFromAvailability(date, frequency, availability);
  // When a HomeHelper is pinned, the day's price must come only from that
  // HomeHelper's slots. Otherwise the calendar advertises a cheaper tier that
  // belongs to a cleaner the user did not choose — and the price shown under a
  // date would not match any slot they can actually book.
  const slots =
    preferredCleanerId != null && preferredCleanerId > 0
      ? allSlots.filter((slot) => slot.cleaners.includes(preferredCleanerId))
      : allSlots;
  if (slots.length === 0) return null;

  let lowest: DayPricingSummary | null = null;

  for (const slot of slots) {
    if (!slot.pricing) continue;
    if (!lowest || slot.pricing.pricePerHrCents < lowest.lowestHourlyRateCents) {
      const tier = slot.pricing.tier as PricingTier;
      lowest = {
        lowestHourlyRateCents: slot.pricing.pricePerHrCents,
        lowestTier: tier,
        lowestTierColor: PRICING_TIERS[tier]?.color ?? PRICING_TIERS.normal.color,
      };
    }
  }

  return lowest;
}
